import {
  Alert,
  Autocomplete,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { AutocompleteMulti } from "components/AutocompleteMulti/AutocompleteMulti"
import { StyledButton } from "components/Button/StyledButton"
import { DeviceTestEquipment, Employee, Skill } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { SyntheticEvent, useEffect, useState } from "react"

type AddSkillDialogProps = {
  open: boolean
  onClose: () => void
  formState: Skill
  onSubmit: (arg: Skill) => void
}

type ValidationErrors = {
  name?: string
}

export const AddSkillDialog = (props: AddSkillDialogProps) => {
  const { open, onClose, formState, onSubmit } = props

  const { employees, testEquipment } = useStoreState((state) => state)
  const { fetchEmployees, fetchTestEquipment } = useStoreActions((actions) => actions)

  const [localFormState, setLocalFormState] = useState<Skill>(formState)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        await fetchEmployees({ fresh: false })
      } catch (err) {
        console.error("Error loading users:", err)
      }
    }
    loadEmployees()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const loadTestEquipment = async () => {
      try {
        await fetchTestEquipment({ fresh: false })
      } catch (err) {
        console.error("Error loading test equipment:", err)
      }
    }
    loadTestEquipment()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setLocalFormState(formState)
    setValidationErrors({}) // Clear errors when form state changes
  }, [formState])

  const validateForm = (): ValidationErrors => {
    const errors: ValidationErrors = {}

    // Check required fields
    if (!localFormState.name?.trim()) {
      errors.name = "Bezeichnung ist erforderlich"
    }

    return errors
  }

  const isFormValid = (): boolean => {
    const errors = validateForm()
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleFormChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target
    if (name) {
      setLocalFormState((prevState) => ({ ...prevState, [name]: value }))

      // Clear validation error for this field
      if (validationErrors[name as keyof ValidationErrors]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[name as keyof ValidationErrors]
          return newErrors
        })
      }
    }
  }

  const handleFormChangeEmployee = (_: SyntheticEvent<Element, Event>, value: Employee[]) => {
    setLocalFormState((prevState) => ({
      ...prevState,
      person: value.map((item) => item.id),
    }))
  }

  const handleFormChangeTe = (_: SyntheticEvent<Element, Event>, value: DeviceTestEquipment[]) => {
    setLocalFormState((prevState) => ({
      ...prevState,
      testEquipment: value,
    }))
  }

  const handleSave = () => {
    if (isFormValid()) {
      onSubmit(localFormState)
    }
  }

  const handleClose = () => {
    setLocalFormState(formState)
    setValidationErrors({})
    onClose()
  }

  const hasValidationErrors = Object.keys(validationErrors).length > 0

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        {localFormState.id !== "NEW" ? "Skill bearbeiten" : "Skill hinzufügen"}
      </DialogTitle>
      <DialogContent>
        {hasValidationErrors && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
              Bitte korrigieren Sie die folgenden Fehler:
            </Typography>
            <Stack spacing={0.5}>
              {Object.entries(validationErrors).map(([field, error]) => (
                <Typography key={field} variant="body2">
                  • {error}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        <TextField
          id="genral"
          size="small"
          name="name"
          label="Bezeichnung"
          type="text"
          fullWidth
          value={localFormState.name}
          onChange={handleFormChange}
          required
          error={!!validationErrors.name}
          helperText={validationErrors.name}
        />

        <Autocomplete
          size="small"
          multiple
          id="tags-outlined"
          options={employees}
          getOptionLabel={(option) => option?.EmployeeName ?? ""}
          filterSelectedOptions
          value={
            localFormState.person && localFormState.person.length > 0
              ? employees.filter((e) => localFormState.person.includes(e.id))
              : []
          }
          sx={{
            "& .MuiAutocomplete-inputRoot": {
              minHeight: "40px",
              maxHeight: "none",
              height: "auto",
              alignItems: "flex-start",
              flexWrap: "wrap",
              overflow: "visible",
              "& .MuiAutocomplete-tag": {
                margin: "2px",
              },
            },
            "& .MuiInputBase-root": {
              flexWrap: "wrap",
              alignItems: "flex-start",
              minHeight: "40px",
              height: "auto",
            },
            "& .MuiOutlinedInput-root": {
              flexWrap: "wrap",
              alignItems: "flex-start",
              minHeight: "40px",
              height: "auto",
            },
          }}
          renderInput={(params) => <TextField {...params} label="Mitarbeiter" />}
          renderOption={(props, option) => (
            <>
              {option && (
                <li {...props} key={option.id}>
                  {option.EmployeeName}
                </li>
              )}
            </>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <>
                {option && (
                  <Chip
                    {...getTagProps({ index })}
                    key={index}
                    label={option.EmployeeName}
                    size="small"
                  />
                )}
              </>
            ))
          }
          onChange={handleFormChangeEmployee}
        />

        <AutocompleteMulti
          options={testEquipment as DeviceTestEquipment[]}
          value={
            testEquipment && testEquipment.length > 0
              ? localFormState.testEquipment.map((p) => p)
              : []
          }
          onChange={handleFormChangeTe}
          getOptionLabel={(option) => `${option.barcode} ${option.name}`}
          label="Testequipment"
        />
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={handleClose} />
        <StyledButton buttonType="save" onClick={handleSave} disabled={hasValidationErrors} />
      </DialogActions>
    </Dialog>
  )
}
